import internalStateService from "../../app/InternalStateService";
import { enableCurrentLocationControl, createSearchField } from "./Controls";
import { drawRoute, drawRouteFromLocation } from "./RouteDrawing";
import { removeOldMarkers, getIEVersion } from "./Validations";
import {
    resolvedPromiseArrayCallback,
    fillPromiseRequests,
    delayedRequests,
    determineDelayedRequestsSliceTo
} from "./ResolveRequests";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

function dynamicallyIncludeScript(id, address) {
    let script = document.createElement("script");
    script.type = "text/javascript";
    script.id = id;
    script.src = address;
    document.body.appendChild(script);

    return script;
}

// Google Map initialization / drawing
function initMap(actionData, dispatch, getState) {
    // We return a promise so we know when to dismiss the loader.
    // This promise only waits fo all callback methods to complete.
    return new Promise((resolve, reject) => {
        let geocoder = new window.google.maps.Geocoder();
        // Bounds object used for storing all the created markers on the map.
        // Once filled we will fit / zoom all the markers on the map / screen.
        let bounds = new window.google.maps.LatLngBounds();
        // An array to store all the promises. This way we know when all the
        // callbacks from the directions service and geocoder service has completed.
        let allRequests = [];
        // We calculate the locations(markers) and directions(for the routing) arrays length.
        // This is made so we calculate them only once and its for performance purposes.
        let locations = actionData.locations ? actionData.locations : [];
        let drawFromLocation = actionData.drawFromLocation;
        let zoom = actionData.zoom;
        let showLocationOnMapsLink = actionData.showLocationOnMapsLink;
        let apiKey = actionData.apiKey;
        let currentLocationControl = actionData.currentLocationControl;
        let firstRequestsCountTo = actionData.firstRequestsCountTo;
        let markersReference = actionData.markersReference || [];
        let drawClosest = actionData.directionToClosestLocation || null;
        let errorMessages = {
            geoLocationError: actionData.geoLocationError,
            geoLocationNotSuported: actionData.geoLocationNotSuported,
            directionsError: actionData.directionsError
        };
        let markerDropAnimation = actionData.markerDropAnimation;
        let markerActionProps = actionData.markerActionProps;
        let map = actionData.map;
        if (!map) {
            map = new window.google.maps.Map(actionData.mapWrapper, {
                gestureHandling: actionData.gestureHandling,
                fullscreenControl: actionData.fullscreenControl,
                streetViewControl: actionData.streetViewControl,
                mapTypeControl: actionData.mapTypeControl,
                zoomControl: false
            });
            actionData.searchBox && createSearchField(map);
        }
        let directionsDisplayRef = actionData.directionsDisplayRef;
        let directions = actionData.directions || {};
        let drawRouteFlag = false;
        let specificLocation = actionData.specificLocation;
        let targetCurrentLocation = actionData.targetCurrentLocation;
        let currentLocationLabel = actionData.currentLocationLabel;
        let callDelayedRequests = actionData.callDelayedRequests;

        let infowindowRef = actionData.infowindowRef;
        if (!infowindowRef) {
            infowindowRef = new window.google.maps.InfoWindow();
        }

        if (
            currentLocationControl &&
            !map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].length
        ) {
            enableCurrentLocationControl({
                map: map,
                zoom: zoom,
                errorMessages: errorMessages,
                apiKey: apiKey,
                currentLocationLabel: currentLocationLabel
            });
        }

        if (directions.end && !drawClosest) {
            // In this case we are drawing from start to end way point
            if (directions.start && !drawFromLocation) {
                allRequests.push(
                    drawRoute(map, directions, errorMessages, directionsDisplayRef).then(
                        response => {
                            drawRouteFlag = response.routed;
                            directionsDisplayRef = response.directionsDisplayRef;
                        }
                    )
                );
            }
            // In this case we are drawing from current device geo location to end way point
            if (drawFromLocation) {
                directions.start = "";
                allRequests.push(
                    drawRouteFromLocation({
                        map: map,
                        directions: directions,
                        errorMessages: errorMessages,
                        directionsDisplayRef: directionsDisplayRef,
                        apiKey: apiKey
                    }).then(response => {
                        drawRouteFlag = response.routed;
                        directionsDisplayRef = response.directionsDisplayRef;
                    })
                );
            }
        }

        removeOldMarkers(locations, markersReference);

        if (callDelayedRequests) {
            let resolver = null;
            let sliceToNewValue = determineDelayedRequestsSliceTo(firstRequestsCountTo, locations);
            let requestLocations = locations.slice(firstRequestsCountTo, sliceToNewValue);
            let promise = new Promise((resolve, reject) => {
                resolver = resolve;
            });

            promise.then(response => {
                resolve(response);
            });

            delayedRequests(
                {
                    locations: locations,
                    requestLocations: requestLocations,
                    allRequests: allRequests,
                    sliceTo: sliceToNewValue,
                    drawRouteFlag: drawRouteFlag,
                    directions: directions,
                    markerDropAnimation: markerDropAnimation,
                    bounds: bounds,
                    geocoder: geocoder,
                    map: map,
                    errorMessages: errorMessages,
                    markersReference: markersReference,
                    showLocationOnMapsLink: showLocationOnMapsLink,
                    markerActionProps: markerActionProps,
                    targetCurrentLocation: targetCurrentLocation,
                    specificLocation: specificLocation,
                    drawClosest: drawClosest,
                    apiKey: apiKey,
                    infowindowRef: infowindowRef,
                    directionsDisplayRef: directionsDisplayRef,
                    dispatch: dispatch,
                    getState: getState
                },
                resolver
            );
        } else {
            let requestLocations = locations.slice(0, firstRequestsCountTo);

            fillPromiseRequests({
                requestLocations: requestLocations,
                allRequests: allRequests,
                drawRouteFlag: drawRouteFlag,
                directions: directions,
                markerDropAnimation: markerDropAnimation,
                bounds: bounds,
                geocoder: geocoder,
                map: map,
                errorMessages: errorMessages,
                markersReference: markersReference,
                showLocationOnMapsLink: showLocationOnMapsLink,
                markerActionProps: markerActionProps,
                dispatch: dispatch
            });

            resolvedPromiseArrayCallback({
                allRequests: allRequests,
                targetCurrentLocation: targetCurrentLocation,
                markersReference: markersReference,
                specificLocation: specificLocation,
                drawClosest: drawClosest,
                errorMessages: errorMessages,
                bounds: bounds,
                apiKey: apiKey,
                map: map,
                infowindowRef: infowindowRef,
                directionsDisplayRef: directionsDisplayRef,
                locations: locations,
                directions: directions,
                drawRouteFlag: drawRouteFlag
            }).then(response => {
                for (let i = 0; i < markersReference.length; i++) {
                    let marker = markersReference[i];
                    bounds.extend({
                        lat: marker.getPosition().lat(),
                        lng: marker.getPosition().lng()
                    });
                }
                response.mapCenter && map.setCenter(response.mapCenter) && map.setZoom(zoom);
                response.mapCenter && map.setZoom(zoom);
                response.fitBounds && map.fitBounds(bounds);
                resolve(response);
            });
        }
    });
}

// The actual action we are dispatching in our component.
export function loadGoogleMap(actionData) {
    return (dispatch, getState) => {
        return new Promise((resolve, reject) => {
            let globalLang = internalStateService.getGlobalProperty("locale");
            let mapScript = document.getElementById("g-maps-api");

            if (mapScript) {
                if (mapScript.src.indexOf(`&language=${globalLang}`) !== -1) {
                    if (window.google && window.google.maps) {
                        initMap(actionData, dispatch, getState).then(result => {
                            resolve(result);
                        });
                    }
                } else {
                    document.body.removeChild(mapScript);
                    delete window.google.maps;
                    mapScript = dynamicallyIncludeScript(
                        "g-maps-api",
                        `https://maps.googleapis.com/maps/api/js?key=${
                            actionData.apiKey
                        }&language=${globalLang}&libraries=places,geometry`
                    );
                }
            } else {
                mapScript = dynamicallyIncludeScript(
                    "g-maps-api",
                    `https://maps.googleapis.com/maps/api/js?key=${
                        actionData.apiKey
                    }&language=${globalLang}&libraries=places,geometry`
                );
            }

            // We are checking for IE 10. For some reason the script,
            // callback doesn't execute when the browser pull it
            // from the cash. This is only on IE10
            if (getIEVersion() === "10") {
                let counter = 0;
                let interval = setInterval(() => {
                    if (window.google && window.google.maps) {
                        clearInterval(interval);
                        initMap(actionData, dispatch, getState).then(result => {
                            resolve(result);
                        });
                    }
                    counter += 50;

                    if (counter === 10000) {
                        clearInterval(interval);
                        WEBUILogs.error(actionData.errorMessages.connectionError);
                    }
                }, 50);
            } else {
                // All other browsers and greater IE versions use the "load" event listener
                mapScript.addEventListener("load", (response, status) => {
                    if (window.google && window.google.maps) {
                        initMap(actionData, dispatch, getState).then(result => {
                            // we are returning the markers in order to
                            // add a click event listener to each one of them
                            resolve(result);
                        });
                    } else {
                        WEBUILogs.error(actionData.errorMessages.connectionError);
                    }
                });
            }
        });
    };
}
